import type {
  AccountDevice,
  Chat,
  ChatJoinRequest,
  DeviceKeyEnvelopeInput,
  DeviceLinkApproval,
  GroupBan,
  PublicDevice,
  PublicGroup,
  Session,
  User,
  UserProfile,
  WireMessage,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(value: string | null): void {
  csrfToken = value;
}

type RequestOptions = Readonly<{
  body?: FormData | unknown;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  signal?: AbortSignal;
}>;

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers = new Headers({ Accept: "application/json" });
  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }
  if (method !== "GET" && csrfToken) headers.set("X-CSRF-Token", csrfToken);
  const init: RequestInit = { method, headers, credentials: "include", cache: "no-store" };
  if (options.signal) init.signal = options.signal;
  if (body !== undefined) init.body = body;
  const response = await fetch(path, init);
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      payload?.error?.message ?? `Ошибка запроса (${response.status})`,
      payload?.error?.code ?? "REQUEST_FAILED",
      response.status,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function loadSession(): Promise<Session | null> {
  try {
    const session = await apiRequest<Session>("/api/auth/session");
    setCsrfToken(session.csrfToken);
    return session;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function login(payload: unknown): Promise<Session> {
  const session = await apiRequest<Session>("/api/auth/login", { method: "POST", body: payload });
  setCsrfToken(session.csrfToken);
  return session;
}

export async function register(payload: unknown): Promise<Session> {
  const session = await apiRequest<Session>("/api/auth/register", { method: "POST", body: payload });
  setCsrfToken(session.csrfToken);
  return session;
}

export type RegistrationMode = "open" | "closed";

export async function loadRegistrationPolicy(): Promise<RegistrationMode> {
  return (await apiRequest<{ mode: RegistrationMode }>("/api/auth/registration-policy")).mode;
}

export async function logout(): Promise<void> {
  await apiRequest<void>("/api/auth/logout", { method: "POST" });
  setCsrfToken(null);
}

export async function getChats(): Promise<Chat[]> {
  return (await apiRequest<{ chats: Chat[] }>("/api/chats")).chats;
}

export async function getPublicGroups(query = ""): Promise<PublicGroup[]> {
  return (await apiRequest<{ chats: PublicGroup[] }>(`/api/chats/public?q=${encodeURIComponent(query)}`)).chats;
}

export async function requestPublicGroupJoin(chatId: string): Promise<void> {
  await apiRequest(`/api/chats/${chatId}/join-requests`, { method: "POST" });
}

export async function getChatJoinRequests(chatId: string): Promise<ChatJoinRequest[]> {
  return (await apiRequest<{ requests: ChatJoinRequest[] }>(`/api/chats/${chatId}/join-requests`)).requests;
}

export async function approveChatJoinRequest(
  chatId: string,
  requestId: string,
  envelopes: ReadonlyArray<Readonly<{ deviceId: string; envelope: string }>>,
): Promise<number> {
  return (await apiRequest<{ epoch: number }>(`/api/chats/${chatId}/join-requests/${requestId}/approve`, {
    method: "POST",
    body: { envelopes },
  })).epoch;
}

export async function rejectChatJoinRequest(chatId: string, requestId: string): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}/join-requests/${requestId}/reject`, { method: "POST" });
}

export async function addChatMember(
  chatId: string,
  userId: string,
  envelopes: ReadonlyArray<Readonly<{ deviceId: string; envelope: string }>>,
): Promise<number> {
  return (await apiRequest<{ epoch: number }>(`/api/chats/${chatId}/members`, {
    method: "POST",
    body: { userId, envelopes },
  })).epoch;
}

export async function removeChatMember(
  chatId: string,
  userId: string,
  envelopes: ReadonlyArray<Readonly<{ deviceId: string; envelope: string }>>,
  ban = false,
  reason?: string,
): Promise<number> {
  return (await apiRequest<{ epoch: number }>(`/api/chats/${chatId}/members/${userId}`, {
    method: "DELETE",
    body: { ban, envelopes, reason },
  })).epoch;
}

export async function setChatMemberRole(chatId: string, userId: string, role: "owner" | "admin" | "member"): Promise<void> {
  await apiRequest(`/api/chats/${chatId}/members/${userId}/role`, { method: "POST", body: { role } });
}

export async function getGroupBans(chatId: string): Promise<GroupBan[]> {
  return (await apiRequest<{ bans: GroupBan[] }>(`/api/chats/${chatId}/bans`)).bans;
}

export async function unblockGroupMember(chatId: string, userId: string): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}/bans/${userId}`, { method: "DELETE" });
}

export async function deleteGroup(chatId: string): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}`, { method: "DELETE" });
}

export type MessagePage = Readonly<{ messages: WireMessage[]; nextBefore: string | null }>;

export async function getMessagePage(chatId: string, before?: string, limit = 50): Promise<MessagePage> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (before) query.set("before", before);
  return apiRequest(`/api/chats/${chatId}/messages?${query}`);
}

export async function getMessages(chatId: string): Promise<WireMessage[]> {
  return (await getMessagePage(chatId, undefined, 100)).messages;
}

export async function updateMessage(chatId: string, messageId: string, wire: WireMessage): Promise<WireMessage> {
  return (await apiRequest<{ message: WireMessage }>(`/api/chats/${chatId}/messages/${messageId}`, {
    method: "PUT", body: wire,
  })).message;
}

export async function deleteMessageForMe(chatId: string, messageId: string): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}/messages/${messageId}`, { method: "DELETE" });
}

export async function retractMessage(chatId: string, messageId: string): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}/messages/${messageId}/retract`, { method: "POST" });
}

export async function setMessageReaction(chatId: string, messageId: string, emoji: string, active: boolean): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}/messages/${messageId}/reactions`, { method: "POST", body: { active, emoji } });
}

export async function setMessagePinned(chatId: string, messageId: string, pinned: boolean): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}/messages/${messageId}/pin`, { method: "POST", body: { pinned } });
}

export async function updateGroupProfile(chatId: string, input: Readonly<{
  avatarColor: string; description: string; name: string; visibility: "private" | "public";
}>): Promise<void> {
  await apiRequest(`/api/chats/${chatId}/profile`, { method: "PUT", body: input });
}

export async function updateGroupAvatar(chatId: string, file: File | null): Promise<void> {
  if (!file) {
    await apiRequest<void>(`/api/chats/${chatId}/avatar`, { method: "DELETE" });
    return;
  }
  const form = new FormData();
  form.append("file", file, file.name);
  await apiRequest<void>(`/api/chats/${chatId}/avatar`, { method: "POST", body: form });
}

export async function updateChatNotifications(chatId: string, level: "all" | "mentions" | "muted", mutedUntil: string | null): Promise<void> {
  await apiRequest<void>(`/api/chats/${chatId}/notifications`, { method: "POST", body: { level, mutedUntil } });
}

export async function createGroupInvite(chatId: string): Promise<{ expiresAt: string; token: string }> {
  return apiRequest(`/api/chats/${chatId}/invites`, { method: "POST", body: { expiresInSeconds: 604800, maxUses: 10 } });
}

export async function claimGroupInvite(token: string): Promise<{ alreadyMember: boolean; chatId: string }> {
  return apiRequest("/api/group-invites/claim", { method: "POST", body: { token } });
}

export async function leaveGroup(chatId: string, envelopes: ReadonlyArray<Readonly<{ deviceId: string; envelope: string }>>): Promise<number> {
  return (await apiRequest<{ epoch: number }>(`/api/chats/${chatId}/leave`, { method: "POST", body: { envelopes } })).epoch;
}

export async function searchUsers(query: string): Promise<User[]> {
  return (await apiRequest<{ users: User[] }>(`/api/users/search?q=${encodeURIComponent(query)}`)).users;
}

export async function getUserProfile(userId: string): Promise<UserProfile> {
  return apiRequest<UserProfile>(`/api/users/${userId}/profile?refresh=${Date.now()}`);
}

export async function updateOwnProfile(input: Readonly<{ avatarColor: string; bio: string; displayName: string }>): Promise<void> {
  await apiRequest<void>("/api/profile", { method: "PUT", body: input });
}

async function uploadProfileImage(path: string, file: File): Promise<void> {
  const form = new FormData();
  form.append("file", file, file.name);
  await apiRequest(path, { method: "POST", body: form });
}

export async function updateOwnAvatar(file: File | null): Promise<void> {
  if (!file) {
    await apiRequest<void>("/api/profile/avatar", { method: "DELETE" });
    return;
  }
  await uploadProfileImage("/api/profile/avatar", file);
}

export async function addOwnProfilePhoto(file: File): Promise<void> {
  await uploadProfileImage("/api/profile/photos", file);
}

export async function deleteOwnProfilePhoto(photoId: string): Promise<void> {
  await apiRequest<void>(`/api/profile/photos/${photoId}`, { method: "DELETE" });
}

export async function reorderOwnProfilePhotos(photoIds: readonly string[]): Promise<void> {
  await apiRequest<void>("/api/profile/photos/order", { method: "PUT", body: { photoIds } });
}

export async function getUserDevices(userId: string): Promise<PublicDevice[]> {
  return (await apiRequest<{ devices: PublicDevice[] }>(`/api/users/${userId}/devices`)).devices;
}

export function activeAccountDevices(devices: readonly AccountDevice[]): AccountDevice[] {
  return devices.filter((device) => device.revokedAt === null);
}

export async function getAccountDevices(): Promise<AccountDevice[]> {
  const result = await apiRequest<{ devices: AccountDevice[] }>(`/api/devices?refresh=${Date.now()}`);
  return activeAccountDevices(result.devices);
}

export async function revokeAccountDevice(
  deviceId: string,
  rekeys: ReadonlyArray<Readonly<{ chatId: string; envelopes: ReadonlyArray<Readonly<{ deviceId: string; envelope: string }>> }>>,
): Promise<void> {
  await apiRequest<void>(`/api/devices/${deviceId}`, { method: "DELETE", body: { rekeys } });
}

export async function importDeviceKeyEnvelopes(envelopes: readonly DeviceKeyEnvelopeInput[]): Promise<number> {
  return (await apiRequest<{ imported: number }>("/api/devices/key-envelopes", {
    method: "POST",
    body: { envelopes },
  })).imported;
}

export async function createDeviceLink(input: Readonly<{
  id: string;
  claimPublicKey: string;
  ciphertext: string;
  nonce: string;
}>): Promise<{ expiresAt: string; id: string }> {
  return apiRequest("/api/device-links", { method: "POST", body: input });
}

export async function requestDeviceLinkApproval(linkId: string, input: unknown): Promise<{
  approvalCode: string;
  claimId: string;
  claimToken: string;
  expiresAt: string;
}> {
  return apiRequest(`/api/device-links/${linkId}/request-approval`, { method: "POST", body: input });
}

export async function exchangeDeviceLinkSession(
  linkId: string,
  claimId: string,
  claimToken: string,
): Promise<Readonly<{ approvalCode: string; state: "pending" }> | (Session & Readonly<{ state: "approved" }>)> {
  const result = await apiRequest<Readonly<{ approvalCode: string; state: "pending" }> | (Session & Readonly<{ state: "approved" }>)>(
    `/api/device-links/${linkId}/exchange-session`,
    { method: "POST", body: { claimId, claimToken } },
  );
  if (result.state === "approved") setCsrfToken(result.csrfToken);
  return result;
}

export async function getDeviceLinkApprovals(): Promise<DeviceLinkApproval[]> {
  return (await apiRequest<{ approvals: DeviceLinkApproval[] }>("/api/device-links/approvals")).approvals;
}

export async function decideDeviceLinkApproval(claimId: string, decision: "approve" | "reject"): Promise<void> {
  await apiRequest<void>(`/api/device-links/approvals/${claimId}/decision`, {
    method: "POST",
    body: { decision },
  });
}

export async function getDeviceLink(linkId: string): Promise<{
  ciphertext: string;
  expiresAt: string;
  nonce: string;
}> {
  return apiRequest(`/api/device-links/${linkId}`);
}

export async function claimDeviceLink(linkId: string, envelopes: readonly DeviceKeyEnvelopeInput[]): Promise<number> {
  return (await apiRequest<{ imported: number }>(`/api/device-links/${linkId}/claim`, {
    method: "POST",
    body: { envelopes },
  })).imported;
}
